// @flow
import * as React from 'react';
import { isAbortError } from './utilities.js';
import { useValidationApiOrigin } from './validation/ValidationApiOriginContext.js';

// This validation isn't as important at authoring time as the equivalent
// check was for Wistia (since you could try loading other peoples' vidoes
// on Wistia). If the bunny video loads in the document, then it's going to be
// in our library. However, it still serves an important role in textbook
// validation over time, as it will catch when people have accidentally
// *removed* videos from Bunny, and thus the document will no longer be
// able to load a video. There is still some utility at authoring time as
// well, since many videos are only launched interactively, and thus authors
// may miss when they've botched an id, as they rarely do thorough checking
// of their work before publishing.
export function useValidateBunnyVideoInLibrary({
  performValidation,
  failedValidation,
  addErrors,
  location,
  videoId,
}: {|
  performValidation: boolean | void,
  failedValidation: boolean,
  addErrors: (errors: Object[]) => void,
  location: {|
    startLineNumber: number,
    startColumn: number,
    endLineNumber: number,
    endColumn: number,
  |},
  videoId: string | void,
|}) {
  const apiOrigin = useValidationApiOrigin();
  React.useEffect(() => {
    if (!performValidation) return;
    if (failedValidation) return;
    // videoId is optional at certain call sites, so will be undefined in
    // those situations. We thus don't want this to trigger an error.
    // At call sites where videoId is mandatory, other spec checks will
    // already be catching the undefined case and prompting the user to
    // provide a videoId prop.
    if (videoId === undefined) return;

    // 🚨 TODO remove this after migratory period
    // We are going to have a special message to help content authors
    // understand when they are putting in a wistia id, when they should
    // be putting in a bunny id. This will help them get acquainted with
    // the new process.
    if (isLikelyWistiaId(videoId)) {
      addErrors([
        {
          message: `${videoId} looks like a Wistia id. You need to use a Bunny id instead. See the mapping spreadsheet if you need to get the Bunny id that corresponds to this Wistia id.`,
          ...location,
        },
      ]);
      return;
    }

    const notInLibraryError = {
      message: `${videoId} is not in our Bunny video library`,
      ...location,
    };

    const indeterminateLibraryExistenceError = {
      message: `${videoId} needs to be in our Bunny video library but we couldn't determine its existence.`,
      ...location,
    };

    const abortController = new AbortController();

    (async function () {
      try {
        const res = await fetch(
          `${apiOrigin}/api/bunny-video-library?videoId=${videoId}`,
          { signal: abortController.signal },
        );
        if (res.ok) {
          const { outcome } = await res.json();
          if (outcome === 'not-in-library') {
            addErrors([notInLibraryError]);
          } else if (outcome === 'could-not-be-determined') {
            addErrors([indeterminateLibraryExistenceError]);
          }
        } else {
          addErrors([indeterminateLibraryExistenceError]);
        }
      } catch (error) {
        if (isAbortError(error)) return;
        addErrors([indeterminateLibraryExistenceError]);
      }
    })();

    return () => {
      abortController.abort();
    };
  });
}

// Wistia ids look like this: gk9w45pd5q
function isLikelyWistiaId(value: string): boolean {
  const wistiaIdRegex = /^[a-z0-9]{10}$/i;
  return wistiaIdRegex.test(value);
}
